import { useState, useEffect } from 'react';

/**
 * Hook to load Google Maps JavaScript API.
 * Loads the script once globally and tracks loading state.
 *
 * Part of Store Location Editor feature (STORE_LOCATION_EDITOR_ENTERPRISE_PLAN).
 *
 * @returns {Object} { isLoaded: boolean, loadError: string|null }
 *
 * @example
 * const { isLoaded, loadError } = useGoogleMaps();
 *
 * if (loadError) return <div>Error: {loadError}</div>;
 * if (!isLoaded) return <div>Loading Google Maps...</div>;
 * return <div>Maps ready!</div>;
 */
export const useGoogleMaps = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    // Check if API key is configured
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setLoadError('Google Maps API key not configured');
      return;
    }

    // Check if script is already loading (prevent duplicate script tags)
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Script exists but not loaded yet, wait for it
      const checkInterval = setInterval(() => {
        if (window.google?.maps) {
          setIsLoaded(true);
          clearInterval(checkInterval);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google?.maps) {
          setLoadError('Timeout loading Google Maps');
        }
      }, 10000);

      return () => clearInterval(checkInterval);
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      setLoadError('Failed to load Google Maps. Please check your API key and network connection.');
    };

    document.head.appendChild(script);

    // Cleanup function (though script should remain loaded)
    return () => {
      // Note: We don't remove the script on unmount as it should be loaded once globally
    };
  }, []);

  return { isLoaded, loadError };
};
